import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import DataGrid from './DataGrid';
import ctaCteApiService from '../../services/CtaCteApiService';

const ESTADO_LABELS = {
  Trial: 'Período de prueba',
  PagoVigente: 'Pago vigente',
  TrialVencido: 'Prueba vencida',
  PagoVencido: 'Pago vencido',
  Suspendido: 'Suspendido'
};

const COBRO_ESTADO_PILL = {
  Aprobado: 'process-success',
  Pendiente: 'process-pending',
  Rechazado: 'process-error',
  Revertido: 'process-error'
};

const formatCurrency = (value, moneda = 'ARS') => new Intl.NumberFormat('es-AR', {
  style: 'currency',
  currency: moneda || 'ARS',
  maximumFractionDigits: 2
}).format(Number(value || 0));

const formatDate = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleDateString('es-AR');
};

const pagoColumns = [
  {
    key: 'fechaAlta',
    header: 'Fecha',
    value: (row) => formatDate(row.fechaCobro || row.fechaAlta),
    exportValue: (row) => formatDate(row.fechaCobro || row.fechaAlta)
  },
  { key: 'tipoCobro', header: 'Tipo' },
  {
    key: 'estadoCobro',
    header: 'Estado',
    render: (row) => (
      <span className={`ctacte-pill ${COBRO_ESTADO_PILL[row.estadoCobro] || 'process-pending'}`}>
        {row.estadoCobro}
      </span>
    ),
    searchValue: (row) => row.estadoCobro,
    exportValue: (row) => row.estadoCobro
  },
  {
    key: 'monto',
    header: 'Monto',
    className: 'ctacte-amount-cell',
    value: (row) => formatCurrency(row.monto, row.moneda),
    exportValue: (row) => formatCurrency(row.monto, row.moneda)
  },
  { key: 'proveedorCobro', header: 'Medio de pago' },
  {
    key: 'referenciaExterna',
    header: 'Referencia',
    value: (row) => row.referenciaExterna || '-',
    exportValue: (row) => row.referenciaExterna || ''
  }
];

const SuscripcionModal = ({ onClose }) => {
  const [suscripcion, setSuscripcion] = useState(null);
  const [pagos, setPagos] = useState([]);
  const [filters, setFilters] = useState({ fechaDesde: '', fechaHasta: '' });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const loadSuscripcion = async () => {
    const data = await ctaCteApiService.getSuscripcion();
    setSuscripcion(data);
  };

  const loadPagos = async (activeFilters = filters) => {
    const data = await ctaCteApiService.getPagos(activeFilters);
    setPagos(Array.isArray(data?.pagos) ? data.pagos : []);
  };

  useEffect(() => {
    let cancelled = false;

    (async () => {
      setLoading(true);
      setError('');
      try {
        await Promise.all([loadSuscripcion(), loadPagos({ fechaDesde: '', fechaHasta: '' })]);
      } catch (err) {
        if (!cancelled) {
          setError(err.message || 'No se pudo cargar la información de la suscripción.');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFilter = async () => {
    setLoading(true);
    setError('');
    try {
      await loadPagos(filters);
    } catch (err) {
      setError(err.message || 'No se pudieron filtrar los pagos.');
    } finally {
      setLoading(false);
    }
  };

  const renderEstadoBanner = () => {
    if (!suscripcion) return null;

    if (suscripcion.enTrial) {
      return (
        <div className="suscripcion-status-banner trial">
          <i className="fas fa-hourglass-half"></i>
          <div>
            <strong>Estás en período de prueba gratuita</strong>
            <p>
              {suscripcion.diasRestantesTrial != null
                ? `Te ${suscripcion.diasRestantesTrial === 1 ? 'queda' : 'quedan'} ${suscripcion.diasRestantesTrial} ${suscripcion.diasRestantesTrial === 1 ? 'día' : 'días'} de prueba.`
                : 'Tu período de prueba está activo.'}
              {suscripcion.fechaFinTrial ? ` Finaliza el ${formatDate(suscripcion.fechaFinTrial)}.` : ''}
            </p>
          </div>
        </div>
      );
    }

    if (suscripcion.estadoAcceso === 'PagoVigente') {
      return (
        <div className="suscripcion-status-banner success">
          <i className="fas fa-circle-check"></i>
          <div>
            <strong>Suscripción activa</strong>
            <p>
              Vigente hasta el {formatDate(suscripcion.fechaFinPagoVigente)}.
            </p>
          </div>
        </div>
      );
    }

    if (suscripcion.estadoAcceso === 'Suspendido') {
      return (
        <div className="suscripcion-status-banner danger">
          <i className="fas fa-triangle-exclamation"></i>
          <div>
            <strong>Cuenta suspendida</strong>
            <p>{suscripcion.mensajeAcceso || 'Tu cuenta fue suspendida por un pago revertido o cancelado.'}</p>
          </div>
        </div>
      );
    }

    return (
      <div className="suscripcion-status-banner warning">
        <i className="fas fa-triangle-exclamation"></i>
        <div>
          <strong>{ESTADO_LABELS[suscripcion.estadoAcceso] || suscripcion.estadoAcceso}</strong>
          <p>{suscripcion.mensajeAcceso || 'Realizá el pago mensual para continuar usando la aplicación.'}</p>
        </div>
      </div>
    );
  };

  const modal = (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content ctacte-detail-modal" onClick={(event) => event.stopPropagation()}>
        <div className="modal-header">
          <h3 className="modal-title">
            <i className="fas fa-crown"></i>
            Suscripción
          </h3>
          <button type="button" className="modal-close-btn" onClick={onClose}>
            <i className="fas fa-times"></i>
          </button>
        </div>

        <div className="modal-body">
          {error && (
            <div className="suscripcion-status-banner danger">
              <i className="fas fa-triangle-exclamation"></i>
              <div><strong>{error}</strong></div>
            </div>
          )}

          {renderEstadoBanner()}

          {suscripcion && (
            <div className="ctacte-kpi-grid compact">
              <article>
                <span>Monto mensual</span>
                <strong>{formatCurrency(suscripcion.montoMensual, suscripcion.moneda)}</strong>
              </article>
              <article>
                <span>Pagos registrados</span>
                <strong>{pagos.length}</strong>
              </article>
            </div>
          )}

          <div className="suscripcion-filters">
            <label>
              Desde
              <input
                type="date"
                value={filters.fechaDesde}
                onChange={(e) => setFilters((prev) => ({ ...prev, fechaDesde: e.target.value }))}
              />
            </label>
            <label>
              Hasta
              <input
                type="date"
                value={filters.fechaHasta}
                onChange={(e) => setFilters((prev) => ({ ...prev, fechaHasta: e.target.value }))}
              />
            </label>
            <button type="button" className="ctacte-secondary-btn" onClick={handleFilter} disabled={loading}>
              <i className="fas fa-filter"></i>
              Filtrar
            </button>
          </div>

          <DataGrid
            title="Historial de pagos"
            rows={pagos}
            columns={pagoColumns}
            rowKey={(row) => row.id}
            emptyText={loading ? 'Cargando pagos...' : 'No hay pagos registrados en el período seleccionado.'}
            searchPlaceholder="Buscar en pagos"
            exportFileName="mis-pagos-suscripcion"
            defaultPageSize={10}
          />
        </div>
      </div>
    </div>
  );

  const target = typeof document !== 'undefined'
    ? document.querySelector('.ctacte-app') || document.body
    : null;

  return target ? createPortal(modal, target) : null;
};

export default SuscripcionModal;
